import { useEffect, useMemo, useRef, useState } from 'react';
import { App } from 'antd';

import {
  useActivateGscMutation,
  useGetGscSelectionSessionQuery,
  useLazyGetGscConnectionStatusQuery,
  type ActivateGscResponse,
  type GscProperty,
} from '../api/gscConnectionApi';
import { isSearchPerformanceCapUpgradable, useSearchPerformanceDomainCap } from '../SearchPerformanceAccess';
import type { GscConnectionDeadEndReason } from '../SearchPerformanceAnalyticsEvents';
import useTrackSearchPerformanceEvent from '../useTrackSearchPerformanceEvent';
import {
  GSC_MAX_SYNCED_DOMAINS,
  type GscActivationConflict,
  describeGscActivationFailure,
  describeNonePairedActivation,
  describePartialActivation,
  httpStatusFromActivationError,
  isGatewayCut,
  parseGscActivationGuardBody,
  toGscActivationFailureReason,
} from './GscActivationErrors';
import { toGscCanonicalDomain } from './GscPropertyDomain';

export const GSC_DOMAIN_PICKER_PAGE_SIZE = 20;

const DEFAULT_ACTIVATION_SUCCESS_MESSAGE = 'Google Search Console connected.';

// Manage Domains only (`committedUrls`). A live GSC source still occupies a
// plan slot after its Prerender domain leaves the session's matched list, and a
// property Google stopped serving (`access_lost`) is not listed at all;
// omitting either dead-ends the page with no row to uncheck. The wizard does
// not pass `committedUrls` and must not invent rows.
function withUnlistedProperties(listed: GscProperty[], urls: string[] | undefined): GscProperty[] {
  if (!urls?.length) {
    return listed;
  }
  const listedUrls = new Set(listed.map((property) => property.siteUrl));
  const unlisted = urls.filter((url) => !listedUrls.has(url)).map((siteUrl): GscProperty => ({ siteUrl }));
  return unlisted.length === 0 ? listed : [...unlisted, ...listed];
}

type Params = {
  sessionId: string;
  sourceId: string;
  onActivated: (selectedUrls: string[]) => void;
  committedUrls?: string[];
  lostUrls?: string[];
  successMessage?: string;
};

export type GscDomainPicker = {
  isFetching: boolean;
  session: ReturnType<typeof useGetGscSelectionSessionQuery>['data'];
  refetch: ReturnType<typeof useGetGscSelectionSessionQuery>['refetch'];
  deadEndReason: GscConnectionDeadEndReason | null;
  lostUrlsGoogleNoLongerLists: string[];
  query: string;
  setQuery: (query: string) => void;
  page: number;
  setPage: (page: number) => void;
  paginated: GscProperty[];
  filteredCount: number;
  selected: string[];
  initialSelected: string[];
  lostUnlistedUrls: string[];
  retainedLostUrls: string[];
  lostRemovals: string[];
  isRelistedLost: (siteUrl: string) => boolean;
  maxDomains: number;
  atLimit: boolean;
  canUpsellDomainCap: boolean;
  isSameDomainBlocked: (siteUrl: string) => boolean;
  toggle: (siteUrl: string) => void;
  isActivating: boolean;
  handleActivate: () => Promise<void>;
  replaceConflicts: GscActivationConflict[] | null;
  dismissReplaceOffer: () => void;
  confirmReplace: () => Promise<void>;
};

export default function useGscDomainPicker({
  sessionId,
  sourceId,
  onActivated,
  committedUrls,
  lostUrls,
  successMessage = DEFAULT_ACTIVATION_SUCCESS_MESSAGE,
}: Params): GscDomainPicker {
  const { message } = App.useApp();
  const trackEvent = useTrackSearchPerformanceEvent();
  const maxDomains = Math.min(useSearchPerformanceDomainCap(), GSC_MAX_SYNCED_DOMAINS);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [initialSelected, setInitialSelected] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [replaceConflicts, setReplaceConflicts] = useState<GscActivationConflict[] | null>(null);
  const [untickedLostUrls, setUntickedLostUrls] = useState<string[]>([]);

  const committed = useMemo(() => new Set(committedUrls ?? []), [committedUrls]);
  const lost = useMemo(() => new Set(lostUrls ?? []), [lostUrls]);

  const { data: session, isFetching, error: sessionError, refetch } = useGetGscSelectionSessionQuery(
    sessionId,
    // Remint returns the same id; without this a remount reuses a cached 4xx.
    { refetchOnMountOrArgChange: true }
  );
  const [activate, { isLoading: isActivating }] = useActivateGscMutation();
  const [fetchStatus] = useLazyGetGscConnectionStatusQuery();

  const lostUnlistedUrls = useMemo(() => {
    if (committedUrls === undefined || !session) {
      return [];
    }
    const listed = new Set(session.properties.map((property) => property.siteUrl));
    return (lostUrls ?? []).filter((url) => !listed.has(url));
  }, [committedUrls, lostUrls, session]);
  const properties = useMemo(
    () =>
      withUnlistedProperties(
        session?.properties ?? [],
        committedUrls === undefined ? undefined : [...committedUrls, ...lostUnlistedUrls]
      ),
    [committedUrls, lostUnlistedUrls, session]
  );
  // Fail open on a missing flag: a rest-api that predates the field must not
  // dead-end healthy accounts during deploy skew.
  const syncableProperties = useMemo(
    () => properties.filter((property) => property.isPermissionSufficient !== false),
    [properties]
  );

  const hasAppliedPreselectionRef = useRef(false);
  useEffect(() => {
    if (hasAppliedPreselectionRef.current || !session) {
      return;
    }
    hasAppliedPreselectionRef.current = true;
    // Wizard: skip unreadable (activate would 403). Manage domains: seed
    // committed ones anyway so they can be unticked — including a live GSC
    // source whose Prerender domain has since dropped out of the matched list.
    const availableUrls = new Set(
      properties
        .filter((property) => {
          if (property.isPermissionSufficient !== false) {
            return true;
          }
          return committedUrls?.includes(property.siteUrl) === true;
        })
        .map((property) => property.siteUrl)
    );
    // Pre-guard connections can carry two spellings of one domain; seeding
    // both arms a guaranteed rejection.
    // Committed URLs are live upstream whatever the plan says, so only the
    // wizard's previous-selection seed is plan-capped.
    const seedCap = committedUrls ? GSC_MAX_SYNCED_DOMAINS : maxDomains;
    const preselected: string[] = [];
    const seededDomains = new Set<string>();
    for (const url of committedUrls ?? session.previouslySelectedUrls ?? []) {
      if (preselected.length >= seedCap) {
        break;
      }
      if (!availableUrls.has(url)) {
        continue;
      }
      const domain = toGscCanonicalDomain(url);
      if (domain !== null && seededDomains.has(domain)) {
        continue;
      }
      if (domain !== null) {
        seededDomains.add(domain);
      }
      preselected.push(url);
    }
    if (preselected.length > 0) {
      setSelected(preselected);
      setInitialSelected(preselected);
    }
  }, [committedUrls, maxDomains, properties, session]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) {
      return properties;
    }
    return properties.filter((property) => property.siteUrl.toLowerCase().includes(needle));
  }, [properties, query]);

  const paginated = useMemo(
    () => filtered.slice((page - 1) * GSC_DOMAIN_PICKER_PAGE_SIZE, page * GSC_DOMAIN_PICKER_PAGE_SIZE),
    [filtered, page]
  );

  useEffect(() => {
    setPage(1);
  }, [query]);

  const atLimit = selected.length >= maxDomains;

  const selectedDomains = useMemo(() => {
    const domains = new Set<string>();
    for (const url of selected) {
      const domain = toGscCanonicalDomain(url);
      if (domain !== null) {
        domains.add(domain);
      }
    }
    return domains;
  }, [selected]);

  const isSameDomainBlocked = (siteUrl: string) => {
    if (selected.includes(siteUrl)) {
      return false;
    }
    const domain = toGscCanonicalDomain(siteUrl);
    return domain !== null && selectedDomains.has(domain);
  };

  // Over every syncable property, not the current page, so the hint does not
  // vanish when the unpicked ones sit on another page.
  const hasMoreSelectableProperties = syncableProperties.some(
    (property) =>
      !selected.includes(property.siteUrl) &&
      !lostUnlistedUrls.includes(property.siteUrl) &&
      !isSameDomainBlocked(property.siteUrl)
  );
  const canUpsellDomainCap = atLimit && isSearchPerformanceCapUpgradable(maxDomains) && hasMoreSelectableProperties;

  // Once per attempt (remount = new attempt), not once per keystroke / toggle.
  const hasTrackedSearchRef = useRef(false);
  useEffect(() => {
    if (!hasTrackedSearchRef.current && query.trim()) {
      hasTrackedSearchRef.current = true;
      trackEvent('Search Performance: Domain Search Used');
    }
  }, [query, trackEvent]);

  const hasTrackedLimitRef = useRef(false);
  useEffect(() => {
    if (!hasTrackedLimitRef.current && atLimit) {
      hasTrackedLimitRef.current = true;
      trackEvent('Search Performance: Selection Limit Reached', { limit: maxDomains });
    }
  }, [atLimit, maxDomains, trackEvent]);

  // `unmatchedSiteUrls` is capped upstream: absence from it proves nothing until it holds every property.
  const lostUrlsGoogleNoLongerLists =
    session && session.unmatchedSiteUrls.length >= session.totalPropertyCount
      ? (lostUrls ?? []).filter((url) => !session.unmatchedSiteUrls.includes(url))
      : [];

  // Keep these distinct: collapsing them made an empty Google account read as
  // a Prerender matching problem.
  const deadEndReason: GscConnectionDeadEndReason | null = (() => {
    if (isFetching) {
      return null;
    }
    if (sessionError) {
      // 4xx = expired/replayed session; 5xx = we couldn't ask, the link may still be fine.
      const status = (sessionError as { status?: unknown }).status;
      return typeof status === 'number' && status >= 400 && status < 500
        ? 'session_expired'
        : 'session_load_failed';
    }
    if (!session) {
      return null;
    }
    if (properties.length === 0) {
      if (lostUrlsGoogleNoLongerLists.length > 0) {
        return 'property_access_lost';
      }
      return session.totalPropertyCount === 0 ? 'no_properties' : 'no_matching_properties';
    }
    if (syncableProperties.length === 0) {
      return 'insufficient_permission';
    }
    return null;
  })();

  const trackedDeadEndsRef = useRef(new Set<GscConnectionDeadEndReason>());
  useEffect(() => {
    if (!deadEndReason || trackedDeadEndsRef.current.has(deadEndReason)) {
      return;
    }
    trackedDeadEndsRef.current.add(deadEndReason);
    trackEvent('Search Performance: Connection Dead End', {
      reason: deadEndReason,
      total_property_count: session?.totalPropertyCount,
    });
  }, [deadEndReason, session, trackEvent]);

  const toggle = (siteUrl: string) => {
    if (lostUnlistedUrls.includes(siteUrl)) {
      setUntickedLostUrls((urls) =>
        urls.includes(siteUrl) ? urls.filter((url) => url !== siteUrl) : [...urls, siteUrl]
      );
      return;
    }
    const isSelected = selected.includes(siteUrl);
    if (!isSelected && atLimit) {
      return;
    }
    const next = isSelected ? selected.filter((url) => url !== siteUrl) : [...selected, siteUrl];
    trackEvent('Search Performance: Property Toggled', {
      action: isSelected ? 'deselected' : 'selected',
      property_url: siteUrl,
      selected_count: next.length,
    });
    setSelected(next);
  };

  const pendingUrls = () => selected.filter((url) => !committed.has(url));

  const finishActivation = (outcome: ActivateGscResponse) => {
    const pairedUrls = outcome.paired.map((p) => p.property_url);
    const failedUrls = outcome.failed.map((p) => p.property_url);
    trackEvent('Search Performance: Domains Activated', {
      selected_count: pairedUrls.length + failedUrls.length,
      paired_count: pairedUrls.length,
      failed_count: failedUrls.length,
      domain: [...pairedUrls, ...failedUrls].join(','),
    });
    if (pairedUrls.length === 0) {
      message.error(describeNonePairedActivation(outcome.failed[0]?.error));
      return;
    }
    if (failedUrls.length > 0) {
      message.warning(describePartialActivation(failedUrls));
    } else {
      message.success(successMessage);
    }
    onActivated(pairedUrls);
  };

  const runActivation = async (replace: boolean) => {
    // Additions only; rest-api skips full pairs itself and fills a live GSC's
    // missing companion. The wizard commits nothing, so this equals `selected`.
    const propertyUrls = pendingUrls();
    const outcome = await activate({
      source_id: sourceId,
      selection_session_id: sessionId,
      property_urls: propertyUrls,
      ...(replace ? { replace: true } : {}),
    }).unwrap();
    // A rest-api still answering 204 (rollout skew) paired everything it accepted.
    finishActivation(outcome ?? { paired: propertyUrls.map((property_url) => ({ property_url })), failed: [] });
  };

  const dismissReplaceOffer = () => setReplaceConflicts(null);

  // The pod keeps pairing after nginx cuts the request at 60s, so status may
  // already show the outcome the response never carried. A property counts
  // only with both halves: the reconnect wizard re-sends URLs whose GSC was
  // already ACTIVE before the request, so GSC alone proves nothing there.
  const recoverFromGatewayCut = async (): Promise<boolean> => {
    try {
      const status = await fetchStatus().unwrap();
      const activeGscUrls = new Set(
        status.sources
          .filter((s) => s.type === 'GSC' && s.status === 'ACTIVE' && s.propertyUrl !== null)
          .map((s) => s.propertyUrl as string)
      );
      const liveCompanionUrls = new Set(
        status.sources
          .filter((s) => s.type === 'PRERENDER' && s.status !== 'DISCONNECTED' && s.propertyUrl !== null)
          .map((s) => s.propertyUrl as string)
      );
      const pending = pendingUrls();
      if (pending.length > 0 && pending.every((url) => activeGscUrls.has(url) && liveCompanionUrls.has(url))) {
        finishActivation({ paired: pending.map((property_url) => ({ property_url })), failed: [] });
        return true;
      }
    } catch {
      // Best-effort read; the generic activation error below still applies.
    }
    return false;
  };

  const handleActivationError = async (err: unknown, replaceOnOffer: boolean) => {
    if (isGatewayCut(err) && (await recoverFromGatewayCut())) {
      return;
    }
    const body = parseGscActivationGuardBody(err);
    const reason = toGscActivationFailureReason(httpStatusFromActivationError(err), body?.error);
    trackEvent('Search Performance: Activation Failed', {
      reason,
      selected_count: selected.length,
      domain: pendingUrls().join(','),
    });
    const conflicts = body?.conflicts ?? [];
    if (
      replaceOnOffer &&
      reason === 'domain_already_connected' &&
      body?.replaceable === true &&
      conflicts.length > 0
    ) {
      setReplaceConflicts(conflicts);
      return;
    }
    message.error(describeGscActivationFailure(reason, { maxDomains: body?.max_domains ?? maxDomains }));
  };

  const confirmReplace = async () => {
    try {
      await runActivation(true);
      dismissReplaceOffer();
    } catch (err) {
      // Don't re-open the same offer; the conflict may have moved underneath it.
      dismissReplaceOffer();
      await handleActivationError(err, false);
    }
  };

  const handleActivate = async () => {
    if (selected.length < 1 || selected.length > maxDomains) {
      message.error(maxDomains === 1 ? 'Select one domain.' : `Select between 1 and ${maxDomains} domains.`);
      return;
    }
    trackEvent('Search Performance: Activate Domains Clicked', {
      selected_count: selected.length,
      domain: pendingUrls().join(','),
    });
    try {
      await runActivation(false);
    } catch (err) {
      await handleActivationError(err, true);
    }
  };

  const untickedRelistedLostUrls =
    committedUrls === undefined
      ? []
      : (lostUrls ?? []).filter((url) => !lostUnlistedUrls.includes(url) && !selected.includes(url));

  return {
    isFetching,
    session,
    refetch,
    deadEndReason,
    lostUrlsGoogleNoLongerLists,
    query,
    setQuery,
    page,
    setPage,
    paginated,
    filteredCount: filtered.length,
    selected,
    initialSelected,
    lostUnlistedUrls,
    retainedLostUrls: lostUnlistedUrls.filter((url) => !untickedLostUrls.includes(url)),
    lostRemovals: [...lostUnlistedUrls.filter((url) => untickedLostUrls.includes(url)), ...untickedRelistedLostUrls],
    isRelistedLost: (siteUrl: string) => lost.has(siteUrl) && !lostUnlistedUrls.includes(siteUrl),
    maxDomains,
    atLimit,
    canUpsellDomainCap,
    isSameDomainBlocked,
    toggle,
    isActivating,
    handleActivate,
    replaceConflicts,
    dismissReplaceOffer,
    confirmReplace,
  };
}
